import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search, User, ChevronRight, QrCode, Users as UsersIcon, Info, LogOut, Loader2, Grid2x2, Download, X,
} from "lucide-react";
import { flightApi, getDeviceToken, setDeviceToken, clearDeviceToken } from "@/lib/flightApi";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/* Register the Flight-scoped PWA manifest + service worker only on these routes,
   so marketing/producer pages are unaffected. */
function useFlightPWA() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/flight.webmanifest";
    link.setAttribute("data-flight", "1");
    document.head.appendChild(link);
    const prevTheme = document.querySelector('meta[name="theme-color"]')?.getAttribute("content");
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "theme-color"; document.head.appendChild(meta); }
    meta.setAttribute("content", "#171C23");
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/flight-sw.js", { scope: "/flight" }).catch(() => {});
    }
    return () => {
      document.head.querySelectorAll('link[data-flight="1"]').forEach((n) => n.remove());
      if (prevTheme && meta) meta.setAttribute("content", prevTheme);
    };
  }, []);
}

function Shell({ children }) {
  return <div className="goose-producer min-h-screen"><div className="mx-auto min-h-screen max-w-md">{children}</div></div>;
}

function Avatar({ photo, size = "h-12 w-12" }) {
  if (photo) return <img src={photo} alt="" className={`${size} shrink-0 rounded-xl object-cover`} />;
  return (
    <div className={`${size} flex shrink-0 items-center justify-center rounded-xl p-inset`}>
      <User className="h-1/2 w-1/2 p-subtle" />
    </div>
  );
}

/* ---------------- Enroll ---------------- */
function EnrollScreen({ token }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const enroll = async () => {
    setBusy(true); setErr("");
    try {
      const model = /iPad/i.test(navigator.userAgent) ? "iPad"
        : /iPhone/i.test(navigator.userAgent) ? "iPhone"
        : /Android/i.test(navigator.userAgent) ? "Android" : "";
      const { data } = await flightApi.post("/flight/enroll", {
        enrollment_token: token, device_name: name.trim() || "Event device", model,
      });
      setDeviceToken(data.device_token);
      toast.success("Device enrolled");
      navigate("/flight", { replace: true });
    } catch (e) {
      setErr(e.response?.data?.detail || "This enrollment link is invalid or has been revoked.");
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="flex min-h-screen flex-col justify-center px-6 py-10">
        <div className="mb-8 text-center">
          <img src="/flight-icon-192.png" alt="" className="mx-auto h-16 w-16 rounded-2xl" />
          <h1 className="font-display mt-4 text-2xl font-bold">Set up Goose Flight Control</h1>
          <p className="mt-2 text-sm p-muted">Name this device and enroll it for event-night access.</p>
        </div>
        <div className="p-card p-6">
          <label className="mb-1.5 block text-xs p-muted">Device name</label>
          <Input data-testid="flight-device-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Front door iPad" />
          {err && <p className="mt-3 text-sm text-red-600" data-testid="flight-enroll-error">{err}</p>}
          <button data-testid="flight-enroll-btn" onClick={enroll} disabled={busy}
            className="p-btn mt-5 flex w-full items-center justify-center gap-2 py-3 text-base">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Enroll this device
          </button>
        </div>
        <p className="mt-6 text-center text-xs p-subtle">Your organizer shared this private link. It only grants access to assigned locations and can be revoked anytime.</p>
      </div>
    </Shell>
  );
}

function NotEnrolled() {
  return (
    <Shell>
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <img src="/flight-icon-192.png" alt="" className="h-16 w-16 rounded-2xl" />
        <h1 className="font-display mt-4 text-2xl font-bold">Goose Flight Control</h1>
        <p className="mt-2 max-w-xs text-sm p-muted" data-testid="flight-not-enrolled">
          This device isn't set up yet. Open the enrollment link your event organizer shared to install Goose.
        </p>
      </div>
    </Shell>
  );
}

/* ---------------- Badges ---------------- */
function Badges({ eventId }) {
  const [q, setQ] = useState("");
  const { data, isFetching } = useQuery({
    queryKey: ["flight-badges", eventId, q],
    queryFn: async () => (await flightApi.get(`/flight/events/${eventId}/badges`, { params: { q } })).data,
    enabled: q.trim().length > 0,
  });
  const results = data?.results || [];
  return (
    <div className="px-5 py-5" data-testid="flight-badges">
      <div className="p-card p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl p-accent-text" style={{ background: "var(--p-accent-soft)" }}>
            <Grid2x2 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold leading-none">Badges</h2>
            <p className="mt-1 text-xs p-subtle">Search for a guest to find their Goose number.</p>
          </div>
        </div>
        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 p-subtle" />
          <Input data-testid="flight-badge-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="pl-9" />
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {q.trim() && !isFetching && results.length === 0 && (
          <p className="px-1 text-sm p-subtle" data-testid="flight-badge-empty">No guest matches that search.</p>
        )}
        {results.map((r) => (
          <div key={r.goose_number} data-testid={`flight-badge-${r.goose_number}`} className="p-card p-5">
            <div className="flex items-center gap-4">
              <Avatar photo={r.photo} size="h-16 w-16" />
              <div>
                <p className="text-sm font-semibold p-muted">Goose Number</p>
                <p className="font-mono-g text-5xl font-black leading-none p-accent-text">#{r.goose_number}</p>
                {r.name && <p className="mt-1 text-sm p-muted">{r.name}</p>}
              </div>
            </div>
            <div className="mt-4 flex items-start gap-2 rounded-lg p-inset p-3 text-xs p-muted">
              <Info className="mt-0.5 h-4 w-4 shrink-0 p-accent-text" />
              This is the guest's Goose number. Use it to look up their compatibility with another guest.
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Introductions ---------------- */
function Introductions({ eventId }) {
  const [preview, setPreview] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["flight-intros", eventId],
    queryFn: async () => (await flightApi.get(`/flight/events/${eventId}/introductions`)).data,
  });
  const intros = data?.introductions || [];

  const pctColor = (s) => (s >= 80 ? "text-emerald-600" : "p-accent-text");

  return (
    <div className="px-5 py-5" data-testid="flight-introductions">
      <div className="p-card p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl text-emerald-600" style={{ background: "rgba(16,185,129,0.12)" }}>
            <UsersIcon className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold leading-none">Introductions</h2>
            <p className="mt-1 text-xs p-subtle">Recommended introductions based on 70%+ compatibility.</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin p-subtle" /></div>
      ) : !data?.ready ? (
        <p className="mt-4 px-1 text-sm p-subtle" data-testid="flight-intros-pending">Introductions appear once the event is Goose Ready.</p>
      ) : intros.length === 0 ? (
        <p className="mt-4 px-1 text-sm p-subtle">No 70%+ introductions for this event.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {intros.map((it, i) => (
            <button key={i} data-testid={`flight-intro-${it.a.goose_number}-${it.b.goose_number}`}
              onClick={() => setPreview(it)}
              className="p-card flex w-full items-center gap-3 p-3 text-left transition-transform active:scale-[0.99]">
              <Avatar photo={it.a.photo} />
              <span className="font-mono-g text-xs font-bold p-subtle">#{it.a.goose_number}</span>
              <div className="flex-1 text-center">
                <p className={`font-mono-g text-2xl font-black leading-none ${pctColor(it.score)}`}>{it.score}%</p>
                <p className="text-xs p-subtle">{it.label}</p>
              </div>
              <span className="font-mono-g text-xs font-bold p-subtle">#{it.b.goose_number}</span>
              <Avatar photo={it.b.photo} />
              <ChevronRight className="h-4 w-4 p-subtle" />
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="goose-producer border-[#E4E7EC] bg-white text-[#171C23]">
          <DialogHeader><DialogTitle className="font-display text-xl">Introduction</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Compatibility preview for this pair by Goose number.</DialogDescription>
          {preview && (
            <div className="flex items-center justify-between gap-4 py-2" data-testid="flight-intro-preview">
              <div className="text-center">
                <Avatar photo={preview.a.photo} size="h-20 w-20" />
                <p className="font-mono-g mt-2 text-sm font-bold">#{preview.a.goose_number}</p>
              </div>
              <div className="text-center">
                <p className={`font-mono-g text-4xl font-black ${pctColor(preview.score)}`}>{preview.score}%</p>
                <p className="text-xs p-subtle">{preview.label}</p>
              </div>
              <div className="text-center">
                <Avatar photo={preview.b.photo} size="h-20 w-20" />
                <p className="font-mono-g mt-2 text-sm font-bold">#{preview.b.goose_number}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InstallHint() {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    try { if (localStorage.getItem("goose_flight_a2hs") === "off") return; } catch { /* ignore */ }
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) return;
    const onBIP = (e) => { e.preventDefault(); setPrompt(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onBIP);
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) setShow(true);
    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);
  const dismiss = () => { setShow(false); try { localStorage.setItem("goose_flight_a2hs", "off"); } catch { /* ignore */ } };
  const install = async () => { if (prompt) { prompt.prompt(); await prompt.userChoice; dismiss(); } };
  if (!show) return null;
  return (
    <div data-testid="a2hs-hint" className="mx-5 mt-4 flex items-center gap-3 rounded-xl p-inset p-3 text-xs p-muted">
      <Download className="h-4 w-4 shrink-0 p-accent-text" />
      <span className="flex-1">{prompt
        ? "Install Goose on this device for quick event-night access."
        : "Add Goose to your Home Screen: tap Share, then “Add to Home Screen”."}</span>
      {prompt && <button data-testid="a2hs-install" onClick={install} className="p-btn px-3 py-1 text-xs">Install</button>}
      <button data-testid="a2hs-dismiss" onClick={dismiss} className="p-subtle"><X className="h-4 w-4" /></button>
    </div>
  );
}

/* ---------------- App shell ---------------- */
function FlightApp() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("badges");
  const [eventId, setEventId] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["flight-context"],
    queryFn: async () => (await flightApi.get("/flight/context")).data,
    retry: false,
  });

  useEffect(() => {
    if (data?.events?.length && !eventId) {
      const ready = data.events.find((e) => e.goose_ready);
      setEventId((ready || data.events[0]).event_id);
    }
  }, [data, eventId]);

  if (isLoading) return <Shell><div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin p-subtle" /></div></Shell>;
  if (isError) { clearDeviceToken(); return <NotEnrolled />; }

  const events = data?.events || [];
  const current = events.find((e) => e.event_id === eventId);

  return (
    <Shell>
      {/* charcoal header */}
      <header className="p-header sticky top-0 z-20">
        <div className="flex items-center gap-3 px-5 py-4">
          <span className="font-display text-lg font-extrabold text-white">GOOSE<span style={{ color: "var(--p-accent)" }}>.</span></span>
          {events.length > 0 && (
            <div className="ml-auto">
              <Select value={eventId || ""} onValueChange={setEventId}>
                <SelectTrigger data-testid="flight-event-select" className="h-8 border-none bg-white/10 text-xs text-white">
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent className="border-[#E4E7EC] bg-white text-[#171C23]">
                  {events.map((e) => <SelectItem key={e.event_id} value={e.event_id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <button data-testid="flight-signout" onClick={() => { clearDeviceToken(); navigate("/flight", { replace: true }); window.location.reload(); }}
            className="flex h-8 w-8 items-center justify-center rounded-full" style={{ color: "var(--p-header-muted)" }} title="Sign out device">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="pb-24">
        <InstallHint />
        {events.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <QrCode className="mx-auto h-8 w-8 p-subtle" />
            <p className="mt-3 text-sm p-muted" data-testid="flight-no-events">No live events at your locations right now.</p>
          </div>
        ) : !current ? null : tab === "badges" ? (
          <Badges eventId={current.event_id} />
        ) : (
          <Introductions eventId={current.event_id} />
        )}
      </main>

      {/* bottom tab bar */}
      {events.length > 0 && current && (
        <nav className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-md border-t p-border-c" style={{ background: "var(--p-card)" }}>
          <div className="grid grid-cols-2">
            {[{ k: "badges", label: "Badges", icon: Grid2x2 }, { k: "introductions", label: "Introductions", icon: UsersIcon }].map((t) => (
              <button key={t.k} data-testid={`flight-tab-${t.k}`} onClick={() => setTab(t.k)}
                className={`flex flex-col items-center gap-1 py-3 text-xs font-semibold ${tab === t.k ? "p-accent-text" : "p-subtle"}`}>
                <t.icon className="h-5 w-5" /> {t.label}
              </button>
            ))}
          </div>
        </nav>
      )}
    </Shell>
  );
}

export default function Flight() {
  useFlightPWA();
  const { token } = useParams();
  if (token) return <EnrollScreen token={token} />;
  if (!getDeviceToken()) return <NotEnrolled />;
  return <FlightApp />;
}
